/**
 * O formato legado, como ele existe hoje no banco.
 *
 * Levantamento dos 16 configs em producao (2026-08-27):
 *
 *   - 15 dos 16 carregam 10 chaves na RAIZ que duplicam o que `fields` ja diz:
 *     showLogo, template, customText, primaryColor, showWorkload, showEventDate,
 *     showEventTime, showProducerName, showEventLocation, showProducerSignature.
 *
 *   - 3 configs tem conflito REAL entre os dois lugares: `fields.showEventLocation`
 *     e false enquanto a raiz diz true. Nao e caso de borda, e producao.
 *
 *   - 9 dos 16 tem `elements: []`. Esses caem no fallback legado em TODOS os
 *     caminhos de render, sempre — nunca tiveram posicionamento por elemento.
 *
 *   - `textPositions.nameTop` aparece com 55 (fabrica) e 28. Nao e sempre o
 *     valor de fabrica, como se supos antes.
 *
 * Nada aqui e "formato antigo que talvez apareca": e o que o editor escreve
 * hoje. A v2 e o alvo; a v1 e o presente.
 */

import type { ElementId } from '../types.js';

export interface V1Position {
  /** Percentual da largura, 0-100. */
  x?: number;
  /** Percentual da altura, 0-100. */
  y?: number;
}

export interface V1Style {
  /** Token do Tailwind: xs | sm | base | lg | xl | 2xl | 3xl | 4xl | 5xl */
  fontSize?: string;
  fontWeight?: string;
  /** Em unidade `em`, como string: "0.2em". */
  letterSpacing?: string;
  uppercase?: boolean;
  opacity?: number;
  textAlign?: string;
  /** Cor propria do elemento. Quando presente, ANULA opacity (regra R1). */
  color?: string;
}

export interface V1Element {
  id?: string;
  enabled?: boolean;
  position?: V1Position;
  style?: V1Style;
  /** Largura absoluta em pixels do canvas de 1123. Ausente = fit-content. */
  width?: number;
}

export interface V1Fields {
  showCPF?: boolean;
  maskCPF?: boolean;
  showQRCode?: boolean;
  showSigners?: boolean;
  showWorkload?: boolean;
  showEventDate?: boolean;
  showEventName?: boolean;
  showEventTime?: boolean;
  showEventLocation?: boolean;
  showParticipantName?: boolean;
  showParticipantLastName?: boolean;
  eventDateMode?: string;
}

export interface V1TextConfig {
  title?: string;
  introText?: string;
  participationText?: string;
  conclusionText?: string;
}

export interface V1Signer {
  name?: string;
  role?: string;
  signatureUrl?: string;
}

export interface V1TextPositions {
  titleTop?: number;
  nameTop?: number;
  metadataTop?: number;
}

export interface V1FontSizes {
  title?: string;
  name?: string;
  event?: string;
}

export interface V1Config {
  fields?: V1Fields;
  signers?: V1Signer[];
  elements?: V1Element[];
  fontSizes?: V1FontSizes;
  textColor?: string;
  textConfig?: V1TextConfig;
  backgroundUrl?: string;
  textPositions?: V1TextPositions;
  workloadHours?: number;
  eligible_tier_ids?: string[];

  // As 10 chaves de raiz. Duplicam `fields` e perdem para ele na precedencia.
  showLogo?: boolean;
  template?: string;
  customText?: string | null;
  primaryColor?: string | null;
  showWorkload?: boolean;
  showEventDate?: boolean;
  showEventTime?: boolean;
  showEventLocation?: boolean;
  showProducerName?: boolean;
  showProducerSignature?: boolean;
}

/** Canvas do editor web (CertificatePreview: CERTIFICATE_WIDTH). */
export const WEB_CANVAS_WIDTH = 1123;
export const WEB_CANVAS_HEIGHT = 794;

export const V1_DEFAULT_TEXT: Required<V1TextConfig> = {
  title: 'CERTIFICADO DE PARTICIPAÇÃO',
  introText: 'Certificamos que',
  participationText: 'participou do evento',
  conclusionText: '',
};

export const V1_DEFAULT_TEXT_POSITIONS: Required<V1TextPositions> = {
  titleTop: 80,
  nameTop: 55,
  metadataTop: 35,
};

export const V1_DEFAULT_FONT_SIZES: Required<V1FontSizes> = {
  title: 'xl',
  name: '4xl',
  event: '2xl',
};

/**
 * Preset de fabrica, identico a `getDefaultElements()` do editor web.
 *
 * Precisa continuar identico: e o ponto de partida da migracao legada, e
 * qualquer divergencia mudaria a aparencia dos 9 configs sem `elements`.
 */
export function v1DefaultElements(): Required<V1Element>[] {
  const el = (
    id: ElementId,
    enabled: boolean,
    x: number,
    y: number,
    style: V1Style,
  ): Required<V1Element> =>
    ({ id, enabled, position: { x, y }, style, width: undefined } as Required<V1Element>);

  return [
    el('title', true, 50, 20, {
      fontSize: 'xl', fontWeight: 'semibold', uppercase: true,
      letterSpacing: '0.05em', textAlign: 'center',
    }),
    el('titleDivider', true, 50, 26, { textAlign: 'center' }),
    el('introText', true, 50, 38, {
      fontSize: 'xs', uppercase: true, letterSpacing: '0.2em',
      textAlign: 'center', opacity: 0.6,
    }),
    el('participantName', true, 50, 45, {
      fontSize: '4xl', fontWeight: 'bold', textAlign: 'center',
    }),
    el('nameUnderline', true, 50, 52, { textAlign: 'center' }),
    el('cpf', false, 50, 55, {
      fontSize: 'xs', uppercase: true, letterSpacing: '0.18em',
      textAlign: 'center', opacity: 0.55,
    }),
    el('participationText', true, 50, 62, {
      fontSize: 'sm', textAlign: 'center', opacity: 0.65,
    }),
    el('eventName', true, 50, 67, {
      fontSize: '2xl', fontWeight: 'semibold', textAlign: 'center',
    }),
    el('metadataRow', true, 50, 74, { textAlign: 'center' }),
    el('conclusionText', true, 50, 82, {
      fontSize: 'xs', textAlign: 'center', opacity: 0.5,
    }),
    el('signatures', true, 15, 90, { textAlign: 'left' }),
    el('validationLabel', true, 85, 88, {
      fontSize: 'xs', uppercase: true, letterSpacing: '0.2em',
      textAlign: 'right', opacity: 0.7,
    }),
    el('validationUrl', true, 85, 91, {
      fontSize: 'xs', textAlign: 'right', opacity: 0.55,
    }),
    el('certificateCode', true, 85, 94, {
      fontSize: 'xs', textAlign: 'right', opacity: 0.35,
    }),
  ];
}

/**
 * Reconstroi `elements` a partir dos tres numeros legados.
 *
 * REPLICA FIEL de `migrateLegacyPositions()` do editor web, incluindo a
 * aritmetica de posicao. Isso e deliberado: 9 configs de producao dependem
 * dela, e mudar a formula aqui mudaria a aparencia deles.
 *
 * Vale registrar o que esta funcao NAO faz, porque e a origem do bug que
 * motivou a reescrita: ela nao conhece cor propria, largura, nem posicao x
 * por elemento. Quando o render caia aqui tendo `elements` disponivel, tudo
 * que o produtor havia ajustado era descartado — e o `conclusionText` ia
 * parar sobre o rodape escuro com opacity 0.5, invisivel.
 *
 * Aqui ela so roda quando `elements` esta MESMO ausente ou vazio.
 */
export function v1MigrateLegacyPositions(
  textPositions: Required<V1TextPositions>,
  textConfig: Required<V1TextConfig>,
  fontSizes: Required<V1FontSizes>,
  fields: V1Fields,
): Required<V1Element>[] {
  const elements = v1DefaultElements();

  const clamp = (n: number) => Math.max(5, Math.min(95, n));
  const titleY = clamp(100 - textPositions.titleTop + 5);
  const nameY = clamp(100 - textPositions.nameTop + 5);
  const metadataY = clamp(100 - textPositions.metadataTop + 5);

  for (const el of elements) {
    switch (el.id as ElementId) {
      case 'title':
        el.position.y = titleY - 5;
        el.enabled = !!textConfig.title;
        if (fontSizes.title) el.style.fontSize = fontSizes.title;
        break;
      case 'titleDivider':
        el.position.y = titleY;
        el.enabled = !!textConfig.title;
        break;
      case 'introText':
        el.position.y = nameY - 10;
        el.enabled = !!textConfig.introText;
        break;
      case 'participantName':
        el.position.y = nameY - 2;
        break;
      case 'nameUnderline':
        el.position.y = nameY + 4;
        break;
      case 'cpf':
        el.position.y = nameY + 8;
        el.enabled = !!fields.showCPF;
        break;
      case 'participationText':
        el.position.y = metadataY - 5;
        el.enabled = !!textConfig.participationText;
        break;
      case 'eventName':
        el.position.y = metadataY;
        break;
      case 'metadataRow':
        el.position.y = metadataY + 7;
        break;
      case 'conclusionText':
        // Pode passar de 100 — o original nao limitava, e nao limitamos aqui
        // para manter a fidelidade. O clamp acontece depois, na normalizacao,
        // que emite VALUE_CLAMPED em vez de mover o elemento em silencio.
        el.position.y = metadataY + 15;
        el.enabled = !!textConfig.conclusionText;
        break;
      case 'signatures':
        el.enabled = fields.showSigners !== false;
        break;
      case 'validationLabel':
      case 'validationUrl':
      case 'certificateCode':
        el.enabled = fields.showQRCode !== false;
        break;
    }
  }

  return elements;
}
